import type { GameId, RoleId } from '@ringmaster/shared';
import type { RoleCardDef } from './HoloRoleCard.js';
import { ONE_NIGHT_ROLE_GUIDE } from '../content/oneNightRoleGuide.js';
import cardFront from '../assets/cards/card-front.png';
import roleBar from '../assets/cards/role-bar.png';
import cardBack from '../assets/games/bloodmoon-card-back.webp';
import cardBackArcana from '../assets/cosmetics/cardback-arcana.webp';
import cardBackEclipse from '../assets/cosmetics/cardback-eclipse.webp';
import seerChar from '../assets/cards/seer-character.png';
import wolfChar from '../assets/cards/wolf-character.png';
import villagerChar from '../assets/cards/villager-character.png';
import deceiverChar from '../assets/cards/deceiver-character.png';
import paladinChar from '../assets/cards/paladin-character.png';
// One Night's own renders (2026-07-29) — before these, THIEF/JOKER borrowed the
// Seer portrait, so three different roles wore one face in the pool strip, the
// how-to-play row and the card flips. That was a comprehension bug, not a
// cosmetic one: a player scanning the pool could not tell them apart.
import thiefChar from '../assets/cards/thief-character.png';
import jokerChar from '../assets/cards/joker-character.png';
// One Night's DEDICATED cast (2026-07-31) — a matched set of 768×810 cutouts
// drawn for this game, replacing the Blood Moon renders it had been borrowing
// for Seer/Villager and the first-pass Thief/Joker. Blood Moon and The Whisper
// keep their own art untouched; only ONE_NIGHT_ROLE_CARD_DEFS points here.
//
// TWO WOLVES, TWO FACES: One Night deals two Werewolf cards, and before this
// they were the same portrait twice — in the gallery that read as a rendering
// bug rather than as a pack. Resolve per copy via `roleCardDefForCopy`.
import onWolf1Char from '../assets/cards/onenight-wolf-1-character.png';
import onWolf2Char from '../assets/cards/onenight-wolf-2-character.png';
import onSeerChar from '../assets/cards/onenight-seer-character.png';
import onVillagerChar from '../assets/cards/onenight-villager-character.png';
import onThiefChar from '../assets/cards/onenight-thief-character.png';
import onJokerChar from '../assets/cards/onenight-joker-character.png';

/**
 * The per-role card art + copy for {@link HoloRoleCard}. Single source shared by
 * the in-game DEAL reveal and the /card-lab preview.
 *
 * Killers (Werewolf) get the blood moon; positive roles (Seer, Villager) get a
 * soft accent glow instead.
 */
export const ROLE_CARD_DEFS: Record<RoleId, RoleCardDef> = {
  SEER: {
    name: 'Seer',
    character: seerChar,
    description: 'Each night, uncover one truth. Win when every wolf has fallen.',
    nameGradient: 'linear-gradient(180deg, #c9b6ff 0%, #8b5cf6 55%, #6a3fd6 100%)',
    glowColor: '#8b5cf6',
    charBottom: '0%',
    charScale: 1.02,
  },
  WEREWOLF: {
    name: 'Werewolf',
    character: wolfChar,
    description: 'Hunt with your pack under cover of night. Win when wolves reach parity.',
    nameGradient: 'linear-gradient(180deg, #ff9d8f 0%, #e0454f 52%, #8f141d 100%)',
    showMoon: true,
    moonColor: '#c1352a',
    glowColor: '#e0454f',
    charBottom: '-2%',
    charScale: 1.06,
  },
  VILLAGER: {
    name: 'Villager',
    character: villagerChar,
    description: 'No special powers — just sharp eyes. Win when every wolf is gone.',
    nameGradient: 'linear-gradient(180deg, #f4dfae 0%, #d9a04a 55%, #a9762f 100%)',
    glowColor: '#d99a48',
    charBottom: '0%',
    charScale: 1.16,
  },
  // ── The Whisper — placeholder cards reusing Blood Moon art in the arcana skin.
  //    Kept as the Record<RoleId, RoleCardDef> fallback for non-Whisper callers;
  //    real Whisper presentation lives in WHISPER_ROLE_CARD_DEFS + roleCardDefFor
  //    below (see THE_WHISPER_SPEC.md §3/§11 for the word-ember/charge framing).
  DECEIVER: {
    name: 'Deceiver',
    character: seerChar,
    description: 'You know the word. Burn the candle before they say it.',
    nameGradient: 'linear-gradient(180deg, #c9b6ff 0%, #8b5cf6 55%, #6a3fd6 100%)',
    glowColor: '#8b5cf6',
    charBottom: '0%',
    charScale: 1.02,
  },
  PALADIN: {
    name: 'Paladin',
    character: villagerChar,
    description: 'Stay a suspect’s hand once each round. Shield the innocent.',
    nameGradient: 'linear-gradient(180deg, #c9b6ff 0%, #8b5cf6 55%, #6a3fd6 100%)',
    glowColor: '#8b5cf6',
    charBottom: '0%',
    charScale: 1.16,
  },
  // ── Blood Moon: One Night's swap roles, in the crimson skin. Kept as the
  //    Record<RoleId, RoleCardDef> fallback for non-One-Night callers; the
  //    game's own presentation lives in ONE_NIGHT_ROLE_CARD_DEFS below.
  THIEF: {
    name: 'Thief',
    character: thiefChar,
    description: 'Swap your card for another player’s — then look. You are what you took.',
    nameGradient: 'linear-gradient(180deg, #f4dfae 0%, #d9a04a 55%, #a9762f 100%)',
    glowColor: '#d99a48',
    charBottom: '0%',
    charScale: 1.02,
  },
  JOKER: {
    name: 'Joker',
    character: jokerChar,
    description: 'Swap two other players’ cards, blind. Neither will know.',
    nameGradient: 'linear-gradient(180deg, #f4dfae 0%, #d9a04a 55%, #a9762f 100%)',
    glowColor: '#d99a48',
    charBottom: '0%',
    charScale: 1.02,
  },
};

/**
 * The Whisper's own role presentation — arcana palette throughout (violet glow,
 * lavender→violet name gradient, no blood moon). Distinct copy from Blood Moon's
 * DECEIVER/PALADIN placeholders above (those stay as the `RoleId`-keyed fallback
 * for any caller that hasn't switched to {@link roleCardDefFor} yet).
 *
 * Character art: dedicated Whisper renders — the masked Deceiver and the
 * sun-shield Paladin (assets/cards/deceiver-character.png / paladin-character.png);
 * the Villager reuses the shared villager render.
 */
const WHISPER_ROLE_CARD_DEFS: Partial<Record<RoleId, RoleCardDef>> = {
  VILLAGER: {
    name: 'Villager',
    character: villagerChar,
    description: 'Interrogate the Seer and say the word before the candle dies.',
    nameGradient: 'linear-gradient(180deg, #c9b6ff 0%, #8b5cf6 55%, #6a3fd6 100%)',
    glowColor: '#8b5cf6',
    charBottom: '0%',
    charScale: 1.16,
  },
  DECEIVER: {
    name: 'Deceiver',
    character: deceiverChar,
    description: 'You already see the word. Blend in, steer them wrong, let the candle die.',
    nameGradient: 'linear-gradient(180deg, #ff6a5e 0%, #c41f1f 55%, #7a0d0d 100%)',
    glowColor: '#c41f1f',
    charBottom: '-2%',
    charScale: 1.04,
  },
  PALADIN: {
    name: 'Paladin',
    character: paladinChar,
    description: 'Once per game: stay a player’s hand — freeze them for 30 s and wipe their queued whispers.',
    nameGradient: 'linear-gradient(180deg, #c9b6ff 0%, #8b5cf6 55%, #6a3fd6 100%)',
    glowColor: '#8b5cf6',
    charBottom: '0%',
    charScale: 1.04,
  },
};

/**
 * One Night's own role presentation — same crimson Blood Moon skin (it's a
 * Blood Moon variant, not a new game skin), but copy specific to the swap
 * roles. WEREWOLF/SEER/VILLAGER fall through to ROLE_CARD_DEFS unchanged.
 * Thief and Joker have their own dedicated renders as of 2026-07-29.
 */
const ONE_NIGHT_ROLE_CARD_DEFS: Partial<Record<RoleId, RoleCardDef>> = {
  // WEREWOLF/SEER/VILLAGER MUST be overridden here, not left to fall through:
  // Blood Moon's copy describes a multi-night elimination game ("each night",
  // "wolves reach parity", "when EVERY wolf is gone") and every clause of it is
  // false in One Night — there is no night kill, no parity check, and the
  // village wins on ONE wolf dying. Playtest flagged the Werewolf card as
  // confusing for exactly this reason.
  //
  // COPY RULE for this game: instructions, not lore. A One Night player reads
  // these WHILE deciding what to tap, in a game that lasts one night, so every
  // line says what the role DOES and what makes it win — no atmosphere, no
  // "under cover of night". The flagship keeps its epic register in
  // ROLE_CARD_DEFS; the difference is deliberate, not drift.
  WEREWOLF: {
    ...ROLE_CARD_DEFS.WEREWOLF,
    character: onWolf1Char,
    description: 'See the other wolf. Alone? Look at one middle card.',
  },
  SEER: {
    ...ROLE_CARD_DEFS.SEER,
    character: onSeerChar,
    description: 'Look at one player’s card, or two middle cards.',
  },
  VILLAGER: {
    ...ROLE_CARD_DEFS.VILLAGER,
    character: onVillagerChar,
    description: 'No night action — just your vote.',
  },
  THIEF: {
    name: 'Thief',
    character: onThiefChar,
    description: 'Swap your card with another player’s, then look at it.',
    nameGradient: 'linear-gradient(180deg, #f4dfae 0%, #d9a04a 55%, #a9762f 100%)',
    glowColor: '#d99a48',
    charBottom: '0%',
    charScale: 1.02,
  },
  JOKER: {
    name: 'Joker',
    character: onJokerChar,
    description: 'Swap two other players’ cards. You do not look.',
    nameGradient: 'linear-gradient(180deg, #f4dfae 0%, #d9a04a 55%, #a9762f 100%)',
    glowColor: '#d99a48',
    charBottom: '0%',
    charScale: 1.02,
  },
};

/**
 * Resolve the right {@link RoleCardDef} for a role given the game it's dealt in.
 * The Whisper gets its own arcana-flavored copy/art (`WHISPER_ROLE_CARD_DEFS`);
 * One Night gets its own swap-role copy (`ONE_NIGHT_ROLE_CARD_DEFS`); every
 * other game (or an unspecified `gameId`) falls back to the shared
 * `ROLE_CARD_DEFS` unchanged — Blood Moon callers are unaffected.
 */
export function roleCardDefFor(gameId: GameId | undefined, roleId: RoleId): RoleCardDef {
  if (gameId === 'whisper') {
    return WHISPER_ROLE_CARD_DEFS[roleId] ?? ROLE_CARD_DEFS[roleId];
  }
  if (gameId === 'onenight') {
    return ONE_NIGHT_ROLE_CARD_DEFS[roleId] ?? ROLE_CARD_DEFS[roleId];
  }
  return ROLE_CARD_DEFS[roleId];
}

/**
 * One Night's two Werewolf portraits. The deck holds TWO wolf cards, so a
 * surface that lays the whole deck out side by side (the role gallery) printed
 * the same face twice, which read as a rendering bug rather than as a pack.
 *
 * Deliberately NOT keyed to a player or a seat: this is presentation for a copy
 * of a CARD in a public deck, and nothing about which face appears may vary
 * with who is looking — that is how a cosmetic becomes a tell.
 */
const ONE_NIGHT_WOLF_FACES = [onWolf1Char, onWolf2Char];

/**
 * {@link roleCardDefFor} for the Nth copy of a role in a laid-out deck.
 *
 * Only One Night's Werewolf has more than one face; every other role (and every
 * other game) ignores `copyIndex` and returns exactly what `roleCardDefFor`
 * would. A third wolf, should a future table size ever deal one, wraps back to
 * the first face — there are two portraits, not N.
 */
export function roleCardDefForCopy(
  gameId: GameId | undefined,
  roleId: RoleId,
  copyIndex: number,
): RoleCardDef {
  const def = roleCardDefFor(gameId, roleId);
  if (gameId !== 'onenight' || roleId !== 'WEREWOLF') return def;
  const face = ONE_NIGHT_WOLF_FACES[copyIndex % ONE_NIGHT_WOLF_FACES.length]!;
  return { ...def, character: face };
}

/**
 * Every role a game can deal, in ORDER OF PLAY — the one list every surface
 * that shows "what's in this game" reads from (the lobby role gallery, One
 * Night's pool strip, the how-to-play role row). It exists because that order
 * was hand-typed in three places and had already started to look like three
 * independent facts; it is one fact.
 *
 * Order of play means: night business first, in the order the game runs it,
 * then the roles with no night at all.
 *  - `onenight` is DERIVED from {@link ONE_NIGHT_ROLE_GUIDE} — the guide stays
 *    the single source of truth for that game (it also carries the copy and the
 *    swap-order chips, so a role added there appears everywhere at once).
 *  - `bloodmoon` follows the phase graph's night order (NIGHT_WOLVES →
 *    NIGHT_SEER); the Villager never wakes, so it comes last.
 *  - `whisper` is the original strip order (re-exported as
 *    `WHISPER_TALLY_ROLES` from ui/WhisperRoleTally.tsx). The game is retired
 *    behind `offered: false`, but legacy rooms can still sit in a whisper
 *    lobby, so it must resolve rather than crash.
 *
 * A NEW GAME REGISTERS HERE: the map is a total `Record<GameId, RoleId[]>`, so
 * adding a game id to `GAME_IDS` fails to typecheck until its play order lands.
 */
export const ROLE_PLAY_ORDER: Record<GameId, RoleId[]> = {
  bloodmoon: ['WEREWOLF', 'SEER', 'VILLAGER'],
  whisper: ['VILLAGER', 'DECEIVER', 'PALADIN'],
  onenight: ONE_NIGHT_ROLE_GUIDE.map((e) => e.role),
};

/** The Whisper's ornate card back (violet all-seeing eye / "Seer's Tapestry"). */
export const WHISPER_CARD_BACK: string = cardBackArcana;

/**
 * One Night's own card back (cardback-eclipse.webp) — a variant of the
 * flagship's back, kept in the same family but visually distinct so a table
 * running One Night never mistakes it for the flagship's deck. Used by the
 * DEAL reveal card, {@link PeekRevealCard}'s passive reveals, and the
 * face-down centre-card slots in hand/OneNightScreens.tsx +
 * discord/desktop/screens/OneNightContent.tsx — all four surfaces share this
 * one constant so the deck reads as one consistent object.
 */
export const ONE_NIGHT_CARD_BACK: string = cardBackEclipse;

/**
 * Resolve the ornate card-BACK art for a game (the face-down state before
 * reveal). Mirrors {@link roleCardDefFor}'s per-game branching so every
 * caller — HoloRoleCard, PeekRevealCard, centre-card slots — picks the same
 * back for the same game with zero duplication.
 */
export function roleCardBackFor(gameId: GameId | undefined): string {
  if (gameId === 'whisper') return WHISPER_CARD_BACK;
  if (gameId === 'onenight') return ONE_NIGHT_CARD_BACK;
  return cardBack;
}

/** Every image the reveal card can render — warm the cache before DEAL. */
export const ROLE_CARD_ART: string[] = [
  cardBack,
  cardBackArcana,
  cardBackEclipse,
  cardFront,
  roleBar,
  deceiverChar,
  paladinChar,
  seerChar,
  wolfChar,
  villagerChar,
];
